<div class="space-y-4">
  <form class="space-y-4" (ngSubmit)="changeCourse(courseForm.value)" [formGroup]="courseForm" novalidate>
    <input type="hidden" formControlName="id">

    @if (showSection('generalDescription')) {
      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="text-base font-semibold text-slate-100">{{ 'course.general-description-tab' | translate }}</h3>

          <div class="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div class="lg:col-span-4">
              <label for="title" class="clp-label">{{ 'course.create.name' | translate }}</label>
              <input type="text" class="clp-input" id="title" formControlName="title" required>
              @if (courseForm.controls['title'].invalid && courseForm.controls['title'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidName' | translate }}
                </div>
              }
            </div>

            <div class="lg:col-span-8">
              <label for="headline" class="clp-label">{{ 'course.create.headline' | translate }}</label>
              <input type="text" class="clp-input" id="headline" formControlName="headline" required>
              @if (courseForm.controls['headline'].invalid && courseForm.controls['headline'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidHeadline' | translate }}
                </div>
              }
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
            <div class="lg:col-span-3">
              <label for="status" class="clp-label">{{ 'course.create.status' | translate }}</label>
              <select class="clp-select" id="status" formControlName="status" required>
                @for (status of courseStatuses; track status) {
                  <option [value]="status.value">{{ status.label | translate }}</option>
                }
              </select>
              @if (courseForm.controls['status'].invalid && courseForm.controls['status'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidStatus' | translate }}
                </div>
              }
            </div>

            <div class="lg:col-span-2">
              <label for="order" class="clp-label">{{ 'course.create.order' | translate }}</label>
              <input type="number" class="clp-input" id="order" formControlName="order" min="1" required>
              @if (courseForm.controls['order'].invalid && courseForm.controls['order'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidOrder' | translate }}
                </div>
              }
            </div>

            <div class="lg:col-span-4">
              <label for="instructor" class="clp-label">{{ 'course.create.instructor' | translate }}</label>
              <input type="text" class="clp-input" id="instructor" formControlName="instructor">
              @if (courseForm.controls['instructor'].invalid && courseForm.controls['instructor'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidInstructor' | translate }}
                </div>
              }
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label for="hours" class="clp-label">{{ 'course.create.hours' | translate }}</label>
              <input type="number" class="clp-input" id="hours" formControlName="hours" min="0" required>
              @if (courseForm.controls['hours'].invalid && courseForm.controls['hours'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidHours' | translate }}
                </div>
              }
            </div>

            <div>
              <label for="limitedDaysAccess" class="clp-label">{{ 'course.create.limitedDaysAccess' | translate }}</label>
              <input type="number" class="clp-input" id="limitedDaysAccess" formControlName="limitedDaysAccess" min="0">
              @if (courseForm.controls['limitedDaysAccess'].invalid && courseForm.controls['limitedDaysAccess'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidLimitedDaysAccess' | translate }}
                </div>
              }
            </div>
          </div>

          <label class="flex items-center gap-2 rounded-md border border-slate-600 bg-slate-900/40 px-3 py-2 text-sm text-slate-200">
            <input
              type="checkbox"
              class="h-4 w-4 rounded border-slate-500 bg-slate-800 text-amber-400 focus:ring-amber-400/50"
              id="issueCertificate"
              formControlName="issueCertificate">
            <span>{{ 'course.create.issueCertificate' | translate }}</span>
          </label>

          <div>
            <label for="summary" class="clp-label">{{ 'course.create.summary' | translate }}</label>
            <textarea class="clp-textarea" id="summary" formControlName="summary" rows="4" required></textarea>
            @if (courseForm.controls['summary'].invalid && courseForm.controls['summary'].touched) {
              <div class="clp-help">
                {{ 'course.create.invalidText' | translate }}
              </div>
            }
          </div>

          <div>
            <label for="about" class="clp-label">{{ 'course.create.about' | translate }}</label>
            <textarea class="clp-textarea" id="about" formControlName="about" rows="5" required></textarea>
            @if (courseForm.controls['about'].invalid && courseForm.controls['about'].touched) {
              <div class="clp-help">
                {{ 'course.create.invalidText' | translate }}
              </div>
            }
          </div>
        </div>
      </section>
    }

    @if (showSection('image')) {
      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="text-base font-semibold text-slate-100">{{ 'course.image-tab' | translate }}</h3>

          <div class="max-w-md">
            <label for="picture" class="clp-label">{{ 'course.create.picture' | translate }}</label>
            @if (courseForm.controls['picture'].invalid && courseForm.controls['picture'].touched) {
              <div class="clp-help">
                {{ 'course.create.invalidPicture' | translate }}
              </div>
            }
            <clp-image-upload
              (imageFile)="pictureChanged($event)"
              (deleteImageFile)="deleteImage()"
              [imageDataUrl]="imageUrl">
            </clp-image-upload>
          </div>
        </div>
      </section>
    }

    @if (showSection('financial')) {
      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="text-base font-semibold text-slate-100">{{ 'course.financial-tab' | translate }}</h3>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
            <div class="lg:col-span-3">
              <label for="fullPrice" class="clp-label">{{ 'course.create.fullPrice' | translate }}</label>
              <input type="number" class="clp-input" id="fullPrice" formControlName="fullPrice" min="0" required>
              @if (courseForm.controls['fullPrice'].invalid && courseForm.controls['fullPrice'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidFullPrice' | translate }}
                </div>
              }
            </div>

            <div class="lg:col-span-3">
              <label for="discountPrice" class="clp-label">{{ 'course.create.discountPrice' | translate }}</label>
              <input type="number" class="clp-input" id="discountPrice" formControlName="discountPrice" min="0" required>
              @if (courseForm.controls['discountPrice'].invalid && courseForm.controls['discountPrice'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidDiscountPrice' | translate }}
                </div>
              }
            </div>

            <div class="lg:col-span-3">
              <label for="currency" class="clp-label">{{ 'course.create.currency' | translate }}</label>
              <select class="clp-select" id="currency" formControlName="currency" required>
                <option value="EUR">{{ 'general.currency.eur' | translate }}</option>
                <option value="USD">{{ 'general.currency.usd' | translate }}</option>
                <option value="BRL">{{ 'general.currency.brl' | translate }}</option>
              </select>
              @if (courseForm.controls['currency'].invalid && courseForm.controls['currency'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidCurrency' | translate }}
                </div>
              }
            </div>

            <div class="lg:col-span-3">
              <label for="installments" class="clp-label">{{ 'course.create.installments' | translate }}</label>
              <input type="number" class="clp-input" id="installments" formControlName="installments" min="1" required>
              @if (courseForm.controls['installments'].invalid && courseForm.controls['installments'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidInstallments' | translate }}
                </div>
              }
            </div>
          </div>
        </div>
      </section>
    }

    @if (showSection('calendar')) {
      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="text-base font-semibold text-slate-100">{{ 'course.calendar-tab' | translate }}</h3>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label for="launchDate" class="clp-label">{{ 'course.create.launchDate' | translate }}</label>
              <p-calendar formControlName="launchDate" styleClass="w-full" inputStyleClass="clp-input" />
              @if (courseForm.controls['launchDate'].invalid && courseForm.controls['launchDate'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidLaunchDate' | translate }}
                </div>
              }
            </div>

            <div>
              <label for="salesStartDate" class="clp-label">{{ 'course.create.salesStartDate' | translate }}</label>
              <p-calendar formControlName="salesStartDate" styleClass="w-full" inputStyleClass="clp-input" />
              @if (courseForm.controls['salesStartDate'].invalid && courseForm.controls['salesStartDate'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidSalesStartDate' | translate }}
                </div>
              }
            </div>

            <div>
              <label for="salesEndDate" class="clp-label">{{ 'course.create.salesEndDate' | translate }}</label>
              <p-calendar formControlName="salesEndDate" styleClass="w-full" inputStyleClass="clp-input" />
              @if (courseForm.controls['salesEndDate'].invalid && courseForm.controls['salesEndDate'].touched) {
                <div class="clp-help">
                  {{ 'course.create.invalidSalesEndDate' | translate }}
                </div>
              }
            </div>
          </div>

          @if (courseForm.errors?.['salesDateInvalid']) {
            <div class="clp-alert clp-alert-danger">
              {{ 'course.create.invalidSalesDateOrder' | translate }}
            </div>
          }
        </div>
      </section>
    }
  </form>
</div>
